import { z } from "zod/v4";

import {
  AgenticLogicProductQueryDescriptions,
  AgenticLogicSourceDefinitionDescriptions,
  AgenticLogicSourceDtoDescriptions,
  AgenticLogicSourceListItemDtoDescriptions,
  AgenticLogicSourceReadDescriptions,
} from "../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../descriptions/types";
import {
  AgenticLogicBestSellersQuerySchema,
  AgenticLogicInStockQuerySchema,
  AgenticLogicNewArrivalsQuerySchema,
} from "../../functions/agentic-logic/sdk/catalog-reads";
import { AgenticLogicProductQuerySchema } from "../../functions/agentic-logic/sdk/product-query";
import {
  AGENTIC_LOGIC_READ_FRESHNESS,
  type AgenticLogicReadFreshness,
  type AgenticLogicReadKind,
} from "../../functions/agentic-logic/runtime/read-freshness";
import {
  AGENTIC_LOGIC_DATASET_DESCRIPTION_MAX_LENGTH,
  AGENTIC_LOGIC_DATASET_KEY_MAX_LENGTH,
  AGENTIC_LOGIC_DATASET_KEY_PATTERN,
  AgenticLogicDependentRuleSchema,
} from "./datasets";

// ---------------------------------------------------------------------------
// Sources — named, org-scoped READ DEFINITIONS behind sdk.getSource(name).
//
// A Source is not data. It names ONE typed SDK read whose answer does not
// depend on the request (best sellers over a window, a collection page, ...).
// Its answer lives only in the shared SDK read cache, under exactly the key
// the equivalent direct read uses — `sdk.getSource("top-sellers")` and
// `sdk.getBestSellers({...same args})` share one entry — and the platform
// refreshes it on a schedule while rules read it. Nothing is versioned.
//
// Contract properties:
//  - The definition is restricted, BY SHAPE, to input-independent reads: no
//    read kind here takes a request product id, a customer, or a cart, so a
//    Source's answer is the same for every shopper in one market. A GraphQL
//    Source stores its document AND its variables, so nothing the request
//    carries can reach it either; what its document may select is checked on
//    write (see `inspectAgenticLogicGraphqlSource`).
//  - Every read kind has a DEFAULT freshness (the platform's own window). A
//    Source may override it either way — shorter or longer — from
//    AGENTIC_LOGIC_SOURCE_MIN_FRESH_MS up to its entry's hard TTL (see
//    `agenticLogicSourceFreshness`).
//  - A Source a PUBLISHED rule reads cannot be deleted (a typed 409 naming
//    the dependent rules, like datasets).
// ---------------------------------------------------------------------------

/** Same lower-kebab vocabulary as dataset keys; names are permanent too. */
export const AGENTIC_LOGIC_SOURCE_NAME_PATTERN =
  AGENTIC_LOGIC_DATASET_KEY_PATTERN;
export const AGENTIC_LOGIC_SOURCE_NAME_MAX_LENGTH =
  AGENTIC_LOGIC_DATASET_KEY_MAX_LENGTH;
export const AGENTIC_LOGIC_SOURCE_DESCRIPTION_MAX_LENGTH =
  AGENTIC_LOGIC_DATASET_DESCRIPTION_MAX_LENGTH;

/**
 * The shortest freshness a Source may ask for. A scheduled refresh spends the
 * shop's Admin budget, so a Source cannot ask to be re-read every few seconds.
 */
export const AGENTIC_LOGIC_SOURCE_MIN_FRESH_MS = 60_000;

/** Sources not read for this long stop being refreshed on a schedule. */
export const AGENTIC_LOGIC_SOURCE_IDLE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The read-cache freshness kind each Source kind's entry lives under — the one
 * the equivalent direct read uses, since they share the entry. node-backend
 * asserts its cache location agrees.
 */
export const AGENTIC_LOGIC_SOURCE_READ_CACHE_KIND: Readonly<
  Record<AgenticLogicSourceReadKind, AgenticLogicReadKind>
> = {
  bestSellers: "shopifyqlRank",
  newArrivals: "adminCatalog",
  inStock: "adminCatalog",
  adminGraphql: "adminCatalog",
  collectionProducts: "storefront",
  storefrontGraphql: "storefront",
};

/**
 * The platform freshness of a Source's read-cache entry. Its `freshMs` is the
 * Source's DEFAULT freshness; its `maxStaleMs` (the entry's hard TTL, how long
 * it is kept at all) is the longest override the Source may set, so an answer
 * a Source still counts as fresh is never already expired. A longer override
 * is refused, never clamped.
 */
export function agenticLogicSourceFreshness(
  kind: AgenticLogicSourceReadKind
): AgenticLogicReadFreshness {
  return AGENTIC_LOGIC_READ_FRESHNESS[
    AGENTIC_LOGIC_SOURCE_READ_CACHE_KIND[kind]
  ];
}

/** A Source's effective freshness: its override, else its read kind's default. */
export function agenticLogicSourceFreshMs(
  kind: AgenticLogicSourceReadKind,
  freshMsOverride: number | null
): number {
  return freshMsOverride ?? agenticLogicSourceFreshness(kind).freshMs;
}

export const AgenticLogicSourceNameSchema = z
  .string()
  .max(AGENTIC_LOGIC_SOURCE_NAME_MAX_LENGTH)
  .regex(
    AGENTIC_LOGIC_SOURCE_NAME_PATTERN,
    "Source names are lower-kebab: a lowercase letter followed by lowercase letters, digits, or hyphens"
  );
export type AgenticLogicSourceName = z.infer<
  typeof AgenticLogicSourceNameSchema
>;

/**
 * A collection page: the `sdk.getProducts` read of one collection, bounded
 * and without post-fetch filtering. Deliberately NO `ids`, `where` or
 * `scan`: ids are what a request supplies, and price filters compile in the
 * cart's presentment currency — both would make the answer request-dependent.
 */
export const AgenticLogicSourceCollectionProductsQuerySchema =
  AgenticLogicProductQuerySchema.pick({
    orderBy: true,
    limit: true,
    include: true,
  })
    .extend({
      collectionId: z
        .string()
        .min(1)
        .describe(AgenticLogicProductQueryDescriptions.collectionId),
    })
    .strict();
export type AgenticLogicSourceCollectionProductsQuery = z.infer<
  typeof AgenticLogicSourceCollectionProductsQuerySchema
>;

/** The longest GraphQL document a Source may store. */
export const AGENTIC_LOGIC_SOURCE_GRAPHQL_MAX_LENGTH = 10_000;

/**
 * A GraphQL Source's read: ONE query document and ALL of its variables,
 * stored. Nothing is supplied at read time except, for Storefront, the
 * runner-owned `$country` — so the answer cannot depend on the request.
 * What the document may select is not expressible in Zod; the write path
 * checks it with `inspectAgenticLogicGraphqlSource` and stores the document
 * in its printed (canonical) form.
 */
export const AgenticLogicSourceGraphqlQuerySchema = z
  .object({
    gql: z.string().min(1).max(AGENTIC_LOGIC_SOURCE_GRAPHQL_MAX_LENGTH),
    variables: z.record(z.string(), z.unknown()),
  })
  .strict();
export type AgenticLogicSourceGraphqlQuery = z.infer<
  typeof AgenticLogicSourceGraphqlQuerySchema
>;

/**
 * The typed read a Source names. Every variant is strict, so an argument the
 * read does not take (a product id, a customer) is refused rather than
 * dropped — a definition that parses is input-independent.
 */
export const AgenticLogicSourceDefinitionSchema = z
  .discriminatedUnion("kind", [
    z
      .object({
        kind: z.literal("bestSellers"),
        args: AgenticLogicBestSellersQuerySchema.strict(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("newArrivals"),
        args: AgenticLogicNewArrivalsQuerySchema.strict(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("inStock"),
        args: AgenticLogicInStockQuerySchema.strict(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("collectionProducts"),
        args: AgenticLogicSourceCollectionProductsQuerySchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal("storefrontGraphql"),
        args: AgenticLogicSourceGraphqlQuerySchema,
      })
      .strict(),
    z
      .object({
        kind: z.literal("adminGraphql"),
        args: AgenticLogicSourceGraphqlQuerySchema,
      })
      .strict(),
  ])
  .describe(AgenticLogicSourceDefinitionDescriptions._schema);
export type AgenticLogicSourceDefinition = z.infer<
  typeof AgenticLogicSourceDefinitionSchema
>;
export type AgenticLogicSourceReadKind = AgenticLogicSourceDefinition["kind"];
export const AgenticLogicSourceReadKindList: readonly AgenticLogicSourceReadKind[] =
  AgenticLogicSourceDefinitionSchema.options.map(
    (option) => option.shape.kind.value
  );

/**
 * What the UI shows for a Source's data:
 *  - `warming`: no answer has been fetched yet (a first refresh is queued).
 *  - `ready`: the cached answer is within its freshness.
 *  - `stale`: the cached answer is older than its freshness; still served
 *    while a refresh renews it.
 *  - `error`: the last scheduled refresh failed (see `lastError`).
 */
export const AgenticLogicSourceStatusList = [
  "warming",
  "ready",
  "stale",
  "error",
] as const;
export const AgenticLogicSourceStatusSchema = z.enum(
  AgenticLogicSourceStatusList
);
export type AgenticLogicSourceStatus = z.infer<
  typeof AgenticLogicSourceStatusSchema
>;

export const AgenticLogicSourceRefreshOutcomeList = ["ok", "error"] as const;
export const AgenticLogicSourceRefreshOutcomeSchema = z.enum(
  AgenticLogicSourceRefreshOutcomeList
);
export type AgenticLogicSourceRefreshOutcome = z.infer<
  typeof AgenticLogicSourceRefreshOutcomeSchema
>;

const AgenticLogicSourceShape = {
  name: AgenticLogicSourceNameSchema.describe(
    AgenticLogicSourceDtoDescriptions.name
  ),
  description: z
    .string()
    .min(1)
    .max(AGENTIC_LOGIC_SOURCE_DESCRIPTION_MAX_LENGTH)
    .describe(AgenticLogicSourceDtoDescriptions.description),
  definition: AgenticLogicSourceDefinitionSchema.describe(
    AgenticLogicSourceDtoDescriptions.definition
  ),
  summary: z.string().describe(AgenticLogicSourceDtoDescriptions.summary),
  freshMs: z
    .number()
    .int()
    .positive()
    .describe(AgenticLogicSourceDtoDescriptions.freshMs),
  freshMsOverride: z
    .number()
    .int()
    .positive()
    .nullable()
    .describe(AgenticLogicSourceDtoDescriptions.freshMsOverride),
  status: AgenticLogicSourceStatusSchema.describe(
    AgenticLogicSourceDtoDescriptions.status
  ),
  fetchedAt: z
    .number()
    .nullable()
    .describe(AgenticLogicSourceDtoDescriptions.fetchedAt),
  lastRefreshedAt: z
    .number()
    .nullable()
    .describe(AgenticLogicSourceDtoDescriptions.lastRefreshedAt),
  lastRefreshStatus: AgenticLogicSourceRefreshOutcomeSchema.nullable().describe(
    AgenticLogicSourceDtoDescriptions.lastRefreshStatus
  ),
  lastError: z
    .string()
    .nullable()
    .describe(AgenticLogicSourceDtoDescriptions.lastError),
  lastReadAt: z
    .number()
    .nullable()
    .describe(AgenticLogicSourceDtoDescriptions.lastReadAt),
  lastUpdateTs: z
    .number()
    .describe(AgenticLogicSourceDtoDescriptions.lastUpdateTs),
};

export const AgenticLogicSourceDtoSchema = z
  .object(AgenticLogicSourceShape)
  .describe(AgenticLogicSourceDtoDescriptions._schema);
export type AgenticLogicSourceDto = z.infer<typeof AgenticLogicSourceDtoSchema>;

export const AgenticLogicSourceListItemDtoSchema = z
  .object({
    ...AgenticLogicSourceShape,
    usedBy: z
      .array(AgenticLogicDependentRuleSchema)
      .describe(AgenticLogicSourceListItemDtoDescriptions.usedBy),
  })
  .describe(AgenticLogicSourceListItemDtoDescriptions._schema);
export type AgenticLogicSourceListItemDto = z.infer<
  typeof AgenticLogicSourceListItemDtoSchema
>;

/**
 * The `data` of a Source delete refused with a 409: published rules read it.
 */
export const AgenticLogicSourceConflictSchema = z.object({
  reason: z.literal("has_dependents"),
  name: AgenticLogicSourceNameSchema,
  dependents: z.array(AgenticLogicDependentRuleSchema),
});
export type AgenticLogicSourceConflict = z.infer<
  typeof AgenticLogicSourceConflictSchema
>;

/**
 * What the runtime `source` SDK read returns: the definition the name
 * resolved to, and the answer of the read it names — exactly the response the
 * equivalent direct read's port call returns, so the SDK shapes it the same
 * way.
 */
export const AgenticLogicSourceReadSchema = z
  .object({
    name: AgenticLogicSourceNameSchema.describe(
      AgenticLogicSourceReadDescriptions.name
    ),
    definition: AgenticLogicSourceDefinitionSchema.describe(
      AgenticLogicSourceReadDescriptions.definition
    ),
    response: z.unknown().describe(AgenticLogicSourceReadDescriptions.response),
  })
  .describe(AgenticLogicSourceReadDescriptions._schema);
export type AgenticLogicSourceRead = z.infer<
  typeof AgenticLogicSourceReadSchema
>;

/**
 * One line naming what a Source reads, for listings, tool output and the UI.
 * Deterministic, so the same definition always reads the same way.
 */
export function describeAgenticLogicSourceDefinition(
  definition: AgenticLogicSourceDefinition
): string {
  const scope = (
    value: { collectionIds?: string[]; tags?: string[] } | undefined
  ): string[] => [
    ...(value?.collectionIds?.length
      ? [`${value.collectionIds.length} collection(s)`]
      : []),
    ...(value?.tags?.length ? [`tags ${value.tags.join(", ")}`] : []),
  ];
  switch (definition.kind) {
    case "bestSellers": {
      const { args } = definition;
      return [
        "Best sellers",
        args.timeWindow ?? "30d",
        `by ${args.metric ?? "units"}`,
        ...scope(args.scope),
        `top ${args.limit}`,
      ].join(" · ");
    }
    case "newArrivals": {
      const { args } = definition;
      return [
        "New arrivals",
        args.timeWindow ?? "30d",
        `by ${args.dateField ?? "created"} date`,
        ...scope(args.scope),
        `top ${args.limit}`,
      ].join(" · ");
    }
    case "inStock": {
      const { args } = definition;
      return [
        "In stock",
        `min ${args.minQuantity ?? 1}`,
        ...scope(args.scope),
        ...(args.orderBy
          ? [`by ${args.orderBy.field} ${args.orderBy.direction ?? "asc"}`]
          : []),
        `top ${args.limit}`,
      ].join(" · ");
    }
    case "collectionProducts": {
      const { args } = definition;
      return [
        "Collection",
        args.collectionId,
        ...(args.orderBy
          ? [`by ${args.orderBy.field} ${args.orderBy.direction ?? "asc"}`]
          : []),
        `first ${args.limit}`,
      ].join(" · ");
    }
    case "storefrontGraphql":
    case "adminGraphql": {
      const { args } = definition;
      const operation = /^\s*query\s+([_A-Za-z][_0-9A-Za-z]*)/.exec(args.gql);
      const variableCount = Object.keys(args.variables).length;
      return [
        definition.kind === "storefrontGraphql"
          ? "Storefront GraphQL"
          : "Admin GraphQL",
        operation?.[1] ?? "anonymous query",
        ...(variableCount > 0 ? [`${variableCount} variable(s)`] : []),
      ].join(" · ");
    }
  }
}

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicSourceDtoDescriptions,
  AgenticLogicSourceDto
>();
assertDescriptions<
  typeof AgenticLogicSourceListItemDtoDescriptions,
  AgenticLogicSourceListItemDto
>();
assertDescriptions<
  typeof AgenticLogicSourceReadDescriptions,
  AgenticLogicSourceRead
>();
